import { useOverlayTrigger } from "@react-aria/overlays";
import type { Emitter } from "mitt";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { useFetcher, useParams, useRouteLoaderData } from "react-router";
import { useOverlayTriggerState } from "react-stately";

import { AuthorizationForm } from "~/features/authorization-form/AuthorizationForm";
import {
  RegistrationForm,
  type RegistrationFormValues,
} from "~/features/registration-form/RegistrationForm";
import { RestorePasswordForm } from "~/features/restore-password-form/RestorePasswordForm";
import { SuccessRestorePasswordForm } from "~/features/success-restore-password-form/SuccessRestorePasswordForm";
import type { loader } from "~/root";
import { Flex } from "~/shared/ui-kit/flex/Flex";
import { Modal } from "~/shared/ui-kit/modal/Modal";
import type {
  LoginCredential,
  RestoreCredential,
} from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";
import { ModalHandlerBlock } from "~/widgets/auth-modals/components/ModalHandlerBlock";

import type { AuthModalName, Events } from "./AuthModalsProvider";

const DEFAULT_MODAL_STATE: Record<AuthModalName, boolean> = {
  register: false,
  login: false,
  restore: false,
  successRestore: false,
};
export const Modals: FC<{
  emitter: Emitter<Events>;
}> = ({ emitter }) => {
  const data = useRouteLoaderData<typeof loader>("root");
  const authFeature = data?.authFeatures;
  const { lang } = useParams();

  const fetcher = useFetcher();

  const [modalState, setModalState] =
    useState<Record<AuthModalName, boolean>>(DEFAULT_MODAL_STATE);

  const open = async (name: AuthModalName): Promise<void> => {
    if (name === "login") {
      await fetcher.submit(null, {
        method: "PUT",
        action: `/api/auth/login?locale=${lang}`,
      });
    }
    setModalState({ ...DEFAULT_MODAL_STATE, [name]: true });
  };

  const close = (): void => {
    setModalState(DEFAULT_MODAL_STATE);
  };

  const registrationModalState = useOverlayTriggerState({
    isOpen: modalState.register,
    onOpenChange: () => (modalState.register ? close() : open("register")),
  });

  const registrationOverlay = useOverlayTrigger(
    { type: "dialog" },
    registrationModalState,
  );
  const loginModalState = useOverlayTriggerState({
    isOpen: modalState.login,
    onOpenChange: () => (modalState.login ? close() : open("login")),
  });

  const loginOverlay = useOverlayTrigger({ type: "dialog" }, loginModalState);

  const restoreModalState = useOverlayTriggerState({
    isOpen: modalState.restore,
    onOpenChange: () => (modalState.restore ? close() : open("restore")),
  });

  const restoreOverlay = useOverlayTrigger(
    { type: "dialog" },
    restoreModalState,
  );
  const successRestoreModalState = useOverlayTriggerState({
    isOpen: modalState.successRestore,
    onOpenChange: () =>
      modalState.successRestore ? close() : open("successRestore"),
  });

  const successRestoreOverlay = useOverlayTrigger(
    { type: "dialog" },
    successRestoreModalState,
  );
  const onRegister = async (
    formData: RegistrationFormValues,
  ): Promise<void> => {
    await fetcher.submit(
      { ...formData, locale: lang ?? "" },
      {
        method: "post",
        action: "/api/auth/register",
      },
    );
  };
  const onLogin = async (formData: LoginCredential): Promise<void> => {
    await fetcher.submit(
      { ...formData, locale: lang ?? "" },
      {
        method: "post",
        action: "/api/auth/login",
      },
    );
  };
  const onRestore = async (formData: RestoreCredential): Promise<void> => {
    await fetcher.submit(
      { ...formData, locale: lang ?? "" },
      {
        method: "post",
        action: `/api/auth/restore-password`,
      },
    );
    open("successRestore");
  };
  useEffect(() => {
    emitter.on("open", open);
    emitter.on("close", close);

    return () => {
      emitter.off("open", open);
      emitter.off("close", close);
    };
  }, []);

  return (
    <>
      {registrationModalState.isOpen && (
        <Modal state={registrationModalState} isDismissable>
          <Flex align="center" flexDirection="column" className="px-2">
            {React.cloneElement(
              <RegistrationForm
                serverErrors={fetcher.data}
                data={{
                  form_title: authFeature?.registration?.[0]?.form_title ?? "",
                  form_description_text:
                    authFeature?.registration?.[0]?.form_description_text ?? "",
                  form_email_input_label:
                    authFeature?.registration?.[0]?.form_email_input_label ??
                    "",
                  form_nickname_input_label:
                    authFeature?.registration?.[0]?.form_nickname_input_label ??
                    "",
                  form_password_input_label:
                    authFeature?.registration?.[0]?.form_password_input_label ??
                    "",
                  form_promo_code_button_text:
                    authFeature?.registration?.[0]
                      ?.form_promo_code_button_text ?? "",
                  form_promo_code_input_label:
                    authFeature?.registration?.[0]
                      ?.form_promo_code_input_label ?? "",
                  form_submit_button_text:
                    authFeature?.registration?.[0]?.form_submit_button_text ??
                    "",
                  validation_invalid_email:
                    authFeature?.registration?.[0]?.validation_invalid_email ??
                    "",
                  validation_required:
                    authFeature?.registration?.[0]?.validation_required ?? "",
                  validation_nickname_max_length:
                    authFeature?.registration?.[0]
                      ?.validation_nickname_max_length ?? "",
                  validation_nickname_min_length:
                    authFeature?.registration?.[0]
                      ?.validation_nickname_min_length ?? "",
                  validation_password_min_width:
                    authFeature?.registration?.[0]
                      ?.validation_password_min_width ?? "",
                  validation_password_max_width:
                    authFeature?.registration?.[0]
                      ?.validation_password_max_width ?? "",
                  description_link_text:
                    authFeature?.registration?.[0]?.description_link_text ?? "",
                  description_text:
                    authFeature?.registration?.[0]?.description_text ?? "",
                }}
                onSubmit={onRegister}
              />,
              registrationOverlay.overlayProps,
            )}

            <ModalHandlerBlock
              descriptionText={
                authFeature?.registration?.[0]?.description_text ?? ""
              }
              descriptionLinkText={
                authFeature?.registration?.[0]?.description_link_text ?? ""
              }
              handleClick={() => open("login")}
            />
          </Flex>
        </Modal>
      )}
      {loginModalState.isOpen && (
        <Modal state={loginModalState} isDismissable>
          <Flex align="center" flexDirection="column" className="px-2">
            {React.cloneElement(
              <AuthorizationForm
                serverError={fetcher.data}
                data={{
                  id: "login",
                  description_link_text:
                    authFeature?.auth?.[0]?.description_link_text ?? "",
                  description_text:
                    authFeature?.auth?.[0]?.description_text ?? "",
                  form_input_label_name:
                    authFeature?.auth?.[0]?.form_input_label_name ?? "",
                  form_input_label_password:
                    authFeature?.auth?.[0]?.form_input_label_password ?? "",
                  form_remember_checkbox_text:
                    authFeature?.auth?.[0]?.form_remember_checkbox_text ?? "",
                  form_restore_link_text:
                    authFeature?.auth?.[0]?.form_restore_link_text ?? "",
                  form_submit_text:
                    authFeature?.auth?.[0]?.form_submit_text ?? "",
                  form_title: authFeature?.auth?.[0]?.form_title ?? "",
                  validation_invalid_credentials:
                    authFeature?.auth?.[0]?.validation_invalid_credentials ??
                    "",
                  validation_required:
                    authFeature?.auth?.[0]?.validation_required ?? "",
                }}
                onSubmit={onLogin}
                onForgotPassword={() => open("restore")}
              />,
              loginOverlay.overlayProps,
            )}
            <ModalHandlerBlock
              descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
              descriptionLinkText={
                authFeature?.auth?.[0]?.description_link_text ?? ""
              }
              handleClick={() => open("register")}
            />
          </Flex>
        </Modal>
      )}
      {restoreModalState.isOpen && (
        <Modal state={restoreModalState} isDismissable>
          <Flex align="center" flexDirection="column" className="px-2">
            {React.cloneElement(
              <RestorePasswordForm
                serverResponse={fetcher.data}
                data={{
                  id: "restore-password",
                  description_link_text:
                    authFeature?.restore_password?.[0]?.description_link_text ??
                    "",
                  description_text:
                    authFeature?.restore_password?.[0]?.description_text ?? "",
                  form_email_input_label:
                    authFeature?.restore_password?.[0]
                      ?.form_email_input_label ?? "",
                  validation_required:
                    authFeature?.restore_password?.[0]?.validation_required ??
                    "",
                  form_submit_button_text:
                    authFeature?.restore_password?.[0]
                      ?.form_submit_button_text ?? "",
                  form_title:
                    authFeature?.restore_password?.[0]?.form_title ?? "",
                  form_description:
                    authFeature?.restore_password?.[0]?.form_description ?? "",
                  validation_invalid:
                    authFeature?.restore_password?.[0]?.validation_invalid ??
                    "",
                }}
                onSubmit={onRestore}
              />,
              restoreOverlay.overlayProps,
            )}

            <ModalHandlerBlock
              descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
              descriptionLinkText={
                authFeature?.auth?.[0]?.description_link_text ?? ""
              }
              handleClick={() => open("register")}
            />
          </Flex>
        </Modal>
      )}

      {successRestoreModalState.isOpen && (
        <Modal state={successRestoreModalState} isDismissable>
          <Flex align="center" flexDirection="column" className="px-2">
            {React.cloneElement(
              <SuccessRestorePasswordForm
                data={{
                  id: "restore-password",
                  description_link_text:
                    authFeature?.restore_password?.[0]?.description_link_text ??
                    "",
                  description_text:
                    authFeature?.restore_password?.[0]?.description_text ?? "",
                  form_email_input_label:
                    authFeature?.restore_password?.[0]
                      ?.form_email_input_label ?? "",
                  validation_required:
                    authFeature?.restore_password?.[0]?.validation_required ??
                    "",
                  form_submit_button_text:
                    authFeature?.restore_password?.[0]
                      ?.form_submit_button_text ?? "",
                  form_title:
                    authFeature?.restore_password?.[0]?.form_title ?? "",
                  form_description:
                    authFeature?.restore_password?.[0]?.form_description ?? "",
                  validation_invalid:
                    authFeature?.restore_password?.[0]?.validation_invalid ??
                    "",
                }}
                onSubmit={close}
              />,
              successRestoreOverlay.overlayProps,
            )}

            <ModalHandlerBlock
              descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
              descriptionLinkText={
                authFeature?.auth?.[0]?.description_link_text ?? ""
              }
              handleClick={() => open("register")}
            />
          </Flex>
        </Modal>
      )}
    </>
  );
};
